import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";

import sortBy from "lodash/fp/sortBy";
import { useIntl } from "react-intl";

import IntegrationService from "@mapmycustomers/shared/enum/integrations/IntegrationService";
import { EntityTypesSupportedByIntegrations } from "@mapmycustomers/shared/types/entity";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Alert, Collapse, CollapsePanel, LoadingSpinner } from "@mapmycustomers/ui";

import { CreateNewCustomField } from "@app/store/integrations";
import {
  hideMatchingFieldsWarning,
  initializeMappingStep,
  toggleNoteSyncing,
} from "@app/store/integrations/actions";
import {
  getCurrentIntegration,
  getIntegrationFields,
  getIntegrationFileSyncing,
  getIntegrationNoteSyncing,
  getLoadingFields,
  isMatchingFieldsWarningVisible,
} from "@app/store/integrations/selectors";
import { RootState } from "@app/store/rootReducer";
import IntegrationField from "@app/types/integrations/IntegrationField";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { getEntityTypeDisplayName } from "@app/util/ui";

import FieldMappingTable from "../FieldMappingTable";
import GroupFieldInfoModal from "../GroupFieldInfoModal";
import MmcFieldInfoModal from "../MmcFieldInfoModal";

import RequiredFieldsInfo from "./RequiredFieldsInfo";
import styles from "./Steps.module.scss";
import StepWrapper from "./StepWrapper";
import SyncOptions from "./SyncOptions";

interface Props {
  hideMatchingFieldsWarning: typeof hideMatchingFieldsWarning;
  initializeMappingStep: typeof initializeMappingStep.request;
  integrationFields: Record<EntityTypesSupportedByIntegrations, IntegrationField[]>;
  loading: boolean;
  service?: IntegrationService;
  serviceName: string;
  syncedEntityTypes: EntityTypesSupportedByIntegrations[];
  warningVisible: boolean;
}

const Step2: React.FC<Props> = ({
  hideMatchingFieldsWarning,
  initializeMappingStep,
  integrationFields,
  loading,
  service,
  serviceName,
  syncedEntityTypes,
  warningVisible,
}) => {
  const intl = useIntl();
  useEffect(() => {
    initializeMappingStep();
  }, [initializeMappingStep]);
  const [mmcFieldInfoModalShowing, showMmcFieldInfoModal, hideMmcFieldInfoModal] = useBoolean();
  const [groupFieldInfoModalShowing, showGroupFieldInfoModal, hideGroupFieldInfoModal] =
    useBoolean();

  const sortedIntegrationFields = useMemo(() => {
    Object.keys(integrationFields).forEach((entityType) => {
      const supportedFieldsForIntegration = new Set(
        Object.values(
          getFieldModelByEntityType(
            entityType as EntityTypesSupportedByIntegrations
          ).getIntegrationCategorizedFields(service)
        )
          .flat()
          .map(({ name }) => name)
      );
      supportedFieldsForIntegration.add(CreateNewCustomField);
      integrationFields[entityType as EntityTypesSupportedByIntegrations] =
        sortBy<IntegrationField>(["crmDisplayName"])(
          integrationFields[entityType as EntityTypesSupportedByIntegrations].map(
            (field: IntegrationField) => ({
              ...field,
              // We need to exclude fields which we not support for integration
              mmcField:
                field.mmcField && supportedFieldsForIntegration.has(field.mmcField)
                  ? field.mmcField
                  : null,
            })
          )
        );
    });
    return integrationFields;
  }, [integrationFields, service]);

  if (loading) {
    return <LoadingSpinner global />;
  }

  return (
    <StepWrapper>
      {warningVisible && (
        <Alert
          afterClose={hideMatchingFieldsWarning}
          className={styles.alert}
          closable
          message={intl.formatMessage(
            {
              id: "integrations.setupStep.fieldsWarning",
              defaultMessage:
                "If you're planning to sync any fields created in Map My Customers (e.g., Territories), <b>please ensure that you have already created the corresponding field in your CRM instance.</b>",
              description: "Fields warning message for setup step",
            },
            {
              b: (text) => <b>{text}</b>,
            }
          )}
          showIcon
          type="warning"
        />
      )}
      <Collapse
        accordion
        className={styles.collapse}
        defaultActiveKey={syncedEntityTypes.length ? syncedEntityTypes[0] : undefined}
      >
        {syncedEntityTypes.map((entityType) => (
          <CollapsePanel
            className={styles.collapseBody}
            header={getEntityTypeDisplayName(intl, entityType, {
              lowercase: false,
              plural: true,
            })}
            key={entityType}
          >
            <RequiredFieldsInfo entityType={entityType} service={service} />
            <SyncOptions entityType={entityType} serviceName={serviceName} />
            <div className={styles.fieldMapperTableWrapper}>
              <FieldMappingTable
                dataset={sortedIntegrationFields[entityType]!}
                entityType={entityType}
                fieldModel={getFieldModelByEntityType(entityType)!}
                onShowGroupFieldInfoModal={showGroupFieldInfoModal}
                onShowMmcFieldInfoModal={showMmcFieldInfoModal}
                serviceName={serviceName}
              />
            </div>
          </CollapsePanel>
        ))}
      </Collapse>
      <MmcFieldInfoModal onHide={hideMmcFieldInfoModal} visible={mmcFieldInfoModalShowing} />
      <GroupFieldInfoModal onHide={hideGroupFieldInfoModal} visible={groupFieldInfoModalShowing} />
    </StepWrapper>
  );
};

const mapStateToProps = (state: RootState) => ({
  fileSyncing: getIntegrationFileSyncing(state),
  integrationFields: getIntegrationFields(state),
  loading: getLoadingFields(state),
  noteSyncing: getIntegrationNoteSyncing(state),
  service: getCurrentIntegration(state)?.type,
  warningVisible: isMatchingFieldsWarningVisible(state),
});

const mapDispatchToProps = {
  hideMatchingFieldsWarning,
  initializeMappingStep: initializeMappingStep.request,
  toggleNoteSyncing,
};

export default connect(mapStateToProps, mapDispatchToProps)(Step2);
